import type { Concert } from './program-schema.ts'
import { concertBounds } from './time.ts'

/**
 * What else is on while this concert plays.
 *
 * Pure and snapshot-only, like the rest of `domain/`: the caller hands in `CONCERTS`,
 * so a clash is visible offline, before you walk across the park to find out.
 *
 * The whole module rests on one judgement — what "at the same time" means when slots
 * are 20 minutes and headline sets are 2½ hours. See `overlapRatio`.
 */

/**
 * Stages whose slots are never offered as a simultaneous *concert*.
 *
 * Biblioteket is talks and podcast recordings, not music. Listing a panel on English
 * wine as the thing you are missing by watching a band is noise. The exclusion is on
 * the candidate side only: a Biblioteket page still names the concerts it clashes
 * with, which is the direction someone deciding between them actually needs.
 */
export const NON_CONCERT_STAGES: readonly string[] = ['Biblioteket']

/**
 * How completely two concerts collide, from 0 (no shared minute) to 1 (one runs
 * entirely inside the other).
 *
 * The shared minutes are divided by the *shorter* concert, not the longer one and not
 * their union. A 2½-hour headline set would otherwise swallow every 40-minute slot
 * around it — each short concert would score a fraction of the long one and fall below
 * any useful threshold, so the page for The Cure would report an empty evening. Against
 * the shorter concert the question becomes the one the reader is asking: how much of
 * *this* concert do I lose by going to that one.
 *
 * Concerts that merely touch score 0. An end that is also a start is a walk between
 * stages, not a clash.
 *
 * `concertBounds` resolves both intervals as absolute Oslo instants, so a different day
 * simply cannot intersect and needs no separate check here, and a midnight crossing is
 * already handled there. An unparseable time yields NaN: every comparison against NaN
 * is false, so the naive arithmetic would return NaN and any `ratio >= threshold` test
 * on it would quietly say "no clash" while `Math.max`/`Math.min` spread the NaN around.
 * Guarding here fails to a plain 0 instead — the same choice `concertStatus` makes when
 * it reports a broken concert as 'kommer' rather than as permanently live.
 */
export function overlapRatio(a: Concert, b: Concert): number {
  const first = concertBounds(a)
  const second = concertBounds(b)

  const aStart = first.start.getTime()
  const aEnd = first.end.getTime()
  const bStart = second.start.getTime()
  const bEnd = second.end.getTime()
  if ([aStart, aEnd, bStart, bEnd].some(Number.isNaN)) return 0

  const shared = Math.min(aEnd, bEnd) - Math.max(aStart, bStart)
  if (shared <= 0) return 0

  const shortest = Math.min(aEnd - aStart, bEnd - bStart)
  // A zero-length concert cannot share a positive number of minutes with anything,
  // so this is unreachable from the guard above — but division stays defined.
  if (shortest <= 0) return 0

  return shared / shortest
}

/**
 * The concerts running at substantially the same time as `concert`, best clash first.
 *
 * Same day, a different stage, and not a talk. The default threshold of half the
 * shorter concert is the product call (KTD8): against the committed snapshot it yields
 * one or two entries for 81 of the 88 slots, three for two of them, and none for five.
 * A concert's own stage is excluded, which also excludes the concert itself.
 *
 * Sorted by ratio descending, then by start time, so the fullest clash leads and two
 * equal ones read in schedule order rather than in snapshot order.
 */
export function simultaneousConcerts(
  all: readonly Concert[],
  concert: Concert,
  threshold = 0.5,
): Concert[] {
  const clashes: { concert: Concert; ratio: number }[] = []

  for (const candidate of all) {
    if (candidate.day !== concert.day) continue
    if (candidate.stage === concert.stage) continue
    if (NON_CONCERT_STAGES.includes(candidate.stage)) continue

    const ratio = overlapRatio(concert, candidate)
    // `> 0` as well as the threshold: a caller passing 0 is asking for a loose match,
    // not for every concert on the day, including the ones that share no minute.
    if (ratio <= 0 || ratio < threshold) continue
    clashes.push({ concert: candidate, ratio })
  }

  clashes.sort((a, b) => b.ratio - a.ratio || a.concert.start.localeCompare(b.concert.start))

  return clashes.map((clash) => clash.concert)
}
